import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Upload,
  FileText,
  Loader2,
  CheckCircle,
  X,
  ArrowRight,
  ArrowLeft,
  Info,
  Trash2,
  Download,
  AlertTriangle,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Sparkles,
} from 'lucide-react';
import toast from 'react-hot-toast';
import axios from 'axios';
import { useQueryClient } from '@tanstack/react-query';
import { useScopeApply, useScopeAnalyzeAsync, useScopeAnalyzeStatus, useSystemSettings } from '../hooks/api';
import { SYSTEM_SETTINGS_DEFAULTS } from '../constants/systemSettingsDefaults';

const STEPS = ['Upload', 'Review', 'Apply'];

const ScopeToTasksWizard = ({ projectId, projectName, onClose, onSuccess }) => {
  const queryClient = useQueryClient();
  const { data: systemSettings } = useSystemSettings();
  const maxSubtaskHours = systemSettings?.max_subtask_hours ?? SYSTEM_SETTINGS_DEFAULTS.max_subtask_hours;
  const [step, setStep] = useState(0);
  const [file, setFile] = useState(null);
  const [sprints, setSprints] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [rawSuggestions, setRawSuggestions] = useState('');
  const [isDragOver, setIsDragOver] = useState(false);
  const [showWhatToInclude, setShowWhatToInclude] = useState(false);
  const [isDownloadingTemplate, setIsDownloadingTemplate] = useState(false);
  const [analyzeStarted, setAnalyzeStarted] = useState(false);
  const [progressStep, setProgressStep] = useState(0); // 0=parsing, 1=AI analysis, 2=preparing, 3=finalizing
  const [expandedTasks, setExpandedTasks] = useState(new Set()); // task indices with description expanded
  const bodyRef = useRef(null);

  const analyzeAsyncMutation = useScopeAnalyzeAsync(projectId);
  const applyMutation = useScopeApply(projectId);

  const [asyncTaskId, setAsyncTaskId] = useState(null);
  const [applyStep, setApplyStep] = useState(0); // 0=idle, 1=creating sprints, 2=creating tasks, 3=done
  const [applyStarted, setApplyStarted] = useState(false); // true as soon as user clicks Create Tasks
  const { data: statusData, isFetching: isPolling } = useScopeAnalyzeStatus(
    projectId,
    asyncTaskId,
    { enabled: !!asyncTaskId }
  );

  const isAnalyzing = analyzeStarted || analyzeAsyncMutation.isLoading || (!!asyncTaskId && (statusData?.status === 'PENDING' || isPolling));
  const isApplying = applyStarted || applyMutation.isLoading;

  // Escape to close (not while applying)
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape' && !isApplying) onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose, isApplying]);

  // Scroll to top when step changes
  useEffect(() => {
    bodyRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, [step]);

  // Progress steps: 0=parsing, 1=AI analysis, 2=preparing, 3=finalizing
  const PROGRESS_STEPS = ['Parsing document', 'AI analysis', 'Preparing suggestions', 'Finalizing'];

  // Update progressStep from async status (real-time from backend)
  React.useEffect(() => {
    if (!asyncTaskId || !statusData) return;
    const msg = (statusData.message || '').toLowerCase();
    if (msg.includes('extracting') || msg.includes('parsing')) setProgressStep(0);
    else if (msg.includes('analyzing') || msg.includes('ai')) setProgressStep(1);
    else if (msg.includes('preparing')) setProgressStep(2);
    if (statusData.status === 'SUCCESS') setProgressStep(3);
  }, [asyncTaskId, statusData]);

  // Reset progress when starting async
  React.useEffect(() => {
    if (analyzeAsyncMutation.isLoading) setProgressStep(0);
  }, [analyzeAsyncMutation.isLoading]);

  const analyzeStatusMessage = PROGRESS_STEPS[progressStep] + (progressStep < 3 ? '...' : '');

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(false);
    const f = e.dataTransfer.files[0];
    if (f && (f.type === 'application/pdf' || f.name.endsWith('.docx'))) {
      setFile(f);
    } else {
      toast.error('Please upload a PDF or Word document');
    }
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    if (!e.currentTarget.contains(e.relatedTarget)) setIsDragOver(false);
  }, []);

  const handleFileSelect = (e) => {
    const f = e.target.files?.[0];
    if (f && (f.type === 'application/pdf' || f.name.endsWith('.docx'))) {
      setFile(f);
    } else if (f) {
      toast.error('Please upload a PDF or Word document');
    }
  };

  const handleAnalyze = async () => {
    if (!file) {
      toast.error('Please select a file first');
      return;
    }
    setAnalyzeStarted(true);
    setProgressStep(0);
    setStep(1);

    try {
      // Always use Celery async flow for real-time progress updates
      const { task_id } = await analyzeAsyncMutation.mutateAsync(file);
      setAsyncTaskId(task_id);
      setAnalyzeStarted(false);
    } catch (err) {
      setAnalyzeStarted(false);
      toast.error(err.response?.data?.detail || 'Failed to analyze scope');
      setStep(0);
    }
  };

  // When async status returns SUCCESS, populate sprints/tasks
  React.useEffect(() => {
    if (statusData?.status === 'SUCCESS') {
      setSprints(statusData.sprints || []);
      setTasks(statusData.tasks || []);
      setRawSuggestions(statusData.raw_suggestions || '');
      setAsyncTaskId(null);
      setAnalyzeStarted(false);
    } else if (statusData?.status === 'FAILURE') {
      setAnalyzeStarted(false);
      toast.error(statusData.error || 'Analysis failed');
      setStep(0);
      setAsyncTaskId(null);
    }
  }, [statusData]);

  const handleDownloadTemplate = useCallback(async () => {
    setIsDownloadingTemplate(true);
    try {
      const response = await axios.get('/api/projects/scope/template', {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'scope_document_template.docx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Template downloaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to download template');
    } finally {
      setIsDownloadingTemplate(false);
    }
  }, []);

  const handleRemoveTask = (idx) => {
    setTasks((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleUpdateTask = (idx, field, value) => {
    setTasks((prev) =>
      prev.map((t, i) => (i === idx ? { ...t, [field]: value } : t))
    );
  };

  // Count includes fallback-expanded subtasks for large tasks without AI subtasks
  const subtaskParentNames = new Set(tasks.filter(t => t.parent_task_name).map(t => t.parent_task_name));
  const totalTasksToCreate = tasks.reduce((sum, t) => {
    const h = t.estimated_hours ?? 8;
    const hasSubtasks = subtaskParentNames.has(t.name);
    if (h <= maxSubtaskHours || hasSubtasks) return sum + 1;
    return sum + 1 + Math.ceil(h / maxSubtaskHours); // parent + subtasks
  }, 0);

  const toggleTaskExpand = (idx) => {
    setExpandedTasks((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const handleApply = async () => {
    setApplyStarted(true);
    const loadingToastId = toast.loading(`Creating ${totalTasksToCreate} tasks...`);
    setApplyStep(1);
    let timer;
    try {
      // Brief visual feedback for "creating sprints" before API completes
      timer = setTimeout(() => setApplyStep(2), 400);
      await applyMutation.mutateAsync({
        sprints: sprints.map((s) => ({
          name: s.name,
          start_date: s.start_date,
          end_date: s.end_date,
          goal: s.goal || '',
        })),
        tasks: tasks.map((t) => ({
          name: t.name,
          description: t.description || t.name,
          estimated_hours: t.estimated_hours ?? 8,
          priority_level: t.priority_level || 'medium',
          tags: t.tags || [],
          sprint_name: t.sprint_name || '',
          due_date: t.due_date || '',
          parent_task_name: t.parent_task_name || '',
        })),
      });
      clearTimeout(timer);
      setApplyStep(3);
      toast.success(`${totalTasksToCreate} tasks created successfully!`, { id: loadingToastId });
      await onSuccess?.();

      // Upload scope document to project documentation
      if (file) {
        try {
          const formData = new FormData();
          formData.append('file', file);
          await axios.post('/api/documentation/upload', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            params: {
              project_id: projectId,
              title: file.name || 'Scope Document',
              is_public: true,
            },
          });
          queryClient.invalidateQueries({ queryKey: ['documentation', projectId] });
          toast.success('Scope document added to project documentation');
        } catch (docErr) {
          // Don't block success - tasks were created; just inform user
          toast.error(docErr.response?.data?.detail || 'Scope doc could not be added to documentation');
        }
      }

      onClose();
    } catch (err) {
      if (timer) clearTimeout(timer);
      setApplyStep(0);
      setApplyStarted(false);
      toast.error(err.response?.data?.detail || 'Failed to create tasks', { id: loadingToastId });
    }
  };

  return (
    <div
      className="modal-backdrop-full flex items-center justify-center p-4 z-[9999]"
      onClick={isApplying ? undefined : onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="scope-wizard-title"
    >
      <div
        className="bg-white dark:bg-secondary-800 rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] max-h-[90dvh] flex flex-col border border-secondary-200 dark:border-secondary-700 animate-in fade-in-0 zoom-in-95"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-secondary-200 dark:border-secondary-700">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 shrink-0 rounded-lg bg-primary-50 dark:bg-primary-900/30 flex items-center justify-center">
              <Sparkles className="h-5 w-5 text-primary-600 dark:text-primary-400" />
            </div>
            <div className="min-w-0">
              <h2 id="scope-wizard-title" className="text-base sm:text-lg font-semibold text-secondary-900 dark:text-white truncate">
                AI Task Creator
              </h2>
              <p className="text-xs text-secondary-500 dark:text-secondary-400 truncate">
                {projectName} • Step {step + 1} of 3
              </p>
            </div>
          </div>
          <button
            onClick={isApplying ? undefined : onClose}
            disabled={isApplying}
            className="p-2 min-h-[44px] min-w-[44px] flex items-center justify-center text-secondary-400 hover:text-secondary-600 hover:bg-secondary-100 dark:hover:bg-secondary-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Progress — dots on mobile, labeled steps on sm+ */}
        <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-secondary-200 dark:border-secondary-700 bg-secondary-50/50 dark:bg-secondary-900/30">
          {/* Mobile: compact dots + current label */}
          <div className="flex sm:hidden flex-col items-center gap-2">
            <div className="flex items-center gap-2">
              {STEPS.map((s, i) => (
                <div
                  key={s}
                  className={`h-2.5 rounded-full transition-all ${
                    i === step ? 'w-6 bg-primary-500' : i < step ? 'w-2.5 bg-primary-600' : 'w-2.5 bg-secondary-300 dark:bg-secondary-600'
                  }`}
                  aria-label={`${s}${i === step ? ' (current)' : ''}`}
                />
              ))}
            </div>
            <span className="text-xs font-medium text-secondary-700 dark:text-secondary-300">
              {STEPS[step]}
            </span>
          </div>
          {/* Desktop: full step indicators */}
          <div className="hidden sm:flex items-center gap-0">
            {STEPS.map((s, i) => (
              <React.Fragment key={s}>
                <div className="flex flex-col items-center">
                  <div
                    className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold transition-all duration-200 ${
                      i < step
                        ? 'bg-primary-600 text-white'
                        : i === step
                        ? 'bg-primary-500 text-white ring-2 ring-primary-400 ring-offset-2 dark:ring-offset-secondary-800'
                        : 'bg-secondary-200 dark:bg-secondary-700 text-secondary-500'
                    }`}
                  >
                    {i < step ? <CheckCircle className="h-5 w-5" /> : i + 1}
                  </div>
                  <span
                    className={`mt-1.5 text-xs font-medium ${
                      i <= step ? 'text-secondary-700 dark:text-secondary-300' : 'text-secondary-400'
                    }`}
                  >
                    {s}
                  </span>
                </div>
                {i < STEPS.length - 1 && (
                  <div className="flex-1 h-0.5 mx-2 mt-[-18px] rounded bg-secondary-200 dark:bg-secondary-700 overflow-hidden">
                    <div
                      className={`h-full rounded transition-all duration-300 ${
                        step > i ? 'w-full bg-primary-500' : 'w-0 bg-transparent'
                      }`}
                    />
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Body */}
        <div ref={bodyRef} className="flex-1 overflow-y-auto p-6 min-h-0">
          {step === 0 && (
            <div className="animate-fade-in-up space-y-5">
              <div
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                className={`border-2 border-dashed rounded-xl p-10 text-center transition-all duration-200 ${
                  isDragOver
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 scale-[1.01]'
                    : file
                    ? 'border-green-400 dark:border-green-600 bg-green-50/50 dark:bg-green-900/10'
                    : 'border-secondary-300 dark:border-secondary-600 hover:border-primary-400 dark:hover:border-primary-500'
                }`}
              >
                <div className={`w-16 h-16 rounded-2xl mx-auto mb-4 flex items-center justify-center ${
                  file ? 'bg-green-100 dark:bg-green-900/30' : isDragOver ? 'bg-primary-100 dark:bg-primary-900/30' : 'bg-secondary-100 dark:bg-secondary-800'
                }`}>
                  <FileText className={`h-8 w-8 ${
                    file ? 'text-green-600 dark:text-green-400' : isDragOver ? 'text-primary-500' : 'text-secondary-500'
                  }`} />
                </div>
                <p className="text-base font-semibold text-secondary-800 dark:text-secondary-200 mb-1">
                  {file ? 'Document ready' : 'Drop your scope document here'}
                </p>
                <p className="text-sm text-secondary-500 mb-4">
                  {file ? file.name : 'PDF or Word (.pdf, .docx)'}
                </p>
                <input
                  type="file"
                  accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  onChange={handleFileSelect}
                  className="hidden"
                  id="scope-file-input"
                />
                <label
                  htmlFor="scope-file-input"
                  className="btn-secondary cursor-pointer inline-flex items-center gap-2 px-4 py-2.5"
                >
                  <Upload className="h-4 w-4" />
                  {file ? 'Choose different file' : 'Browse files'}
                </label>
              </div>
              {file && (
                <p className="text-sm text-secondary-600 dark:text-secondary-400">
                  Click <strong>Analyze</strong> below to extract sprints and tasks from your document.
                </p>
              )}
              <div className="flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => setShowWhatToInclude(!showWhatToInclude)}
                  className="text-xs text-primary-600 dark:text-primary-400 hover:underline flex items-center gap-1"
                >
                  <Info className="h-3.5 w-3.5" />
                  What to include for best results
                </button>
                <button
                  type="button"
                  onClick={handleDownloadTemplate}
                  disabled={isDownloadingTemplate}
                  className="text-xs text-primary-600 dark:text-primary-400 hover:underline flex items-center gap-1 disabled:opacity-50"
                >
                  <Download className="h-3.5 w-3.5" />
                  {isDownloadingTemplate ? 'Downloading...' : 'Download template'}
                </button>
              </div>
              {showWhatToInclude && (
                <div className="mt-2 p-3 bg-secondary-50 dark:bg-secondary-900/50 rounded-lg text-xs text-secondary-600 dark:text-secondary-400 space-y-1">
                  <p>• Deliverables, features, milestones</p>
                  <p>• Phases, sprints, timeline with dates</p>
                  <p>• Priorities (must-have, critical)</p>
                  <p>• Effort estimates (hours, story points)</p>
                  <p>• Categories for tags</p>
                </div>
              )}
            </div>
          )}

          {step === 1 && (
            <div className="animate-fade-in-up space-y-4 overflow-y-auto min-h-0">
              {isAnalyzing ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <Loader2 className="h-10 w-10 text-primary-500 animate-spin mb-4" />
                  <p className="text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                    {analyzeStatusMessage}
                  </p>
                  <div className="flex flex-col gap-2 mt-4 text-xs">
                    {PROGRESS_STEPS.map((label, i) => (
                      <span
                        key={label}
                        className={`flex items-center gap-2 transition-colors ${
                          i < progressStep ? 'text-green-600 dark:text-green-400' : i === progressStep ? 'text-primary-600 dark:text-primary-400 font-medium' : 'text-secondary-400'
                        }`}
                      >
                        <span
                          className={`w-1.5 h-1.5 rounded-full ${
                            i < progressStep ? 'bg-green-500' : i === progressStep ? 'bg-primary-500 animate-pulse' : 'bg-secondary-300'
                          }`}
                        />
                        {label}
                        {i < progressStep && ' ✓'}
                      </span>
                    ))}
                  </div>
                </div>
              ) : (
                <>
                  <p className="text-sm text-secondary-600 dark:text-secondary-400 mb-4">
                    Review and edit the suggestions below. Add descriptions where needed, then click <strong>Continue</strong>.
                    Large tasks are broken down into meaningful subtasks by the AI.
                  </p>
                  {sprints.length > 0 && (
                    <div>
                      <h3 className="text-sm font-semibold text-secondary-700 dark:text-secondary-300 mb-2">
                        Sprints ({sprints.length})
                      </h3>
                      <div className="space-y-2">
                        {sprints.map((s, i) => (
                          <div
                            key={i}
                            className="p-3 bg-secondary-50 dark:bg-secondary-900/50 rounded-lg border-l-4 border-l-primary-500"
                          >
                            <p className="font-medium text-secondary-900 dark:text-white text-sm">{s.name}</p>
                            <p className="text-xs text-secondary-500 mt-0.5">
                              {String(s.start_date).slice(0, 10)} – {String(s.end_date).slice(0, 10)}
                            </p>
                            {s.goal && (
                              <p className="text-xs text-secondary-600 dark:text-secondary-400 mt-1 line-clamp-2">{s.goal}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <div>
                    <h3 className="text-sm font-semibold text-secondary-700 dark:text-secondary-300 mb-3">
                      Tasks ({tasks.length})
                    </h3>
                    <div className="space-y-2 pb-4">
                      {tasks.map((t, i) => {
                        const isExpanded = expandedTasks.has(i);
                        const hasDescription = !!(t.description?.trim());
                        return (
                          <div
                            key={i}
                            className="rounded-lg border border-secondary-200 dark:border-secondary-600 bg-white dark:bg-secondary-800/80 overflow-hidden transition-shadow hover:shadow-sm"
                          >
                            <div className="p-3 flex gap-3 items-start">
                              <div className="flex-1 min-w-0">
                                <textarea
                                  value={t.name}
                                  onChange={(e) => handleUpdateTask(i, 'name', e.target.value)}
                                  rows={2}
                                  className="w-full font-medium text-sm border-0 bg-transparent focus:ring-0 p-0 resize-none focus:outline-none text-secondary-900 dark:text-white placeholder-secondary-400 leading-snug"
                                  placeholder="Task name"
                                />
                                <div className="flex items-center gap-2 mt-2 flex-wrap">
                                  <span className="text-xs bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 px-2 py-0.5 rounded font-medium">
                                    {t.estimated_hours ?? 8}h
                                  </span>
                                  {t.parent_task_name && (
                                    <span className="text-xs bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 px-2 py-0.5 rounded font-medium truncate max-w-[120px]" title={`Subtask of: ${t.parent_task_name}`}>
                                      ↳ {t.parent_task_name}
                                    </span>
                                  )}
                                  {t.sprint_name && (
                                    <span className="text-xs text-secondary-500 truncate max-w-[140px]" title={t.sprint_name}>
                                      {t.sprint_name}
                                    </span>
                                  )}
                                  <button
                                    type="button"
                                    onClick={() => toggleTaskExpand(i)}
                                    className="text-xs text-primary-600 dark:text-primary-400 hover:underline flex items-center gap-0.5"
                                  >
                                    {isExpanded ? (
                                      <>
                                        <ChevronUp className="h-3.5 w-3.5" />
                                        Hide description
                                      </>
                                    ) : hasDescription ? (
                                      <>
                                        <ChevronDown className="h-3.5 w-3.5" />
                                        Edit description
                                      </>
                                    ) : (
                                      <>
                                        <ChevronDown className="h-3.5 w-3.5" />
                                        Add description
                                      </>
                                    )}
                                  </button>
                                </div>
                              </div>
                              <button
                                onClick={() => handleRemoveTask(i)}
                                className="p-2 text-secondary-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors flex-shrink-0"
                                title="Remove task"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                            {isExpanded && (
                              <div className="px-3 pb-3 pt-0 border-t border-secondary-100 dark:border-secondary-700/50 mt-0">
                                <textarea
                                  value={t.description ?? ''}
                                  onChange={(e) => handleUpdateTask(i, 'description', e.target.value)}
                                  rows={3}
                                  className="w-full text-sm border border-secondary-200 dark:border-secondary-600 rounded-lg px-3 py-2 bg-secondary-50 dark:bg-secondary-900/50 text-secondary-700 dark:text-secondary-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
                                  placeholder="Add a description for this task..."
                                />
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  {/* Raw suggestions / AI notes - often contains warnings or caveats */}
                  {rawSuggestions && (
                    <div className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                      <AlertCircle className="h-4 w-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                      <p className="text-xs text-amber-800 dark:text-amber-200">{rawSuggestions}</p>
                    </div>
                  )}
                  {/* Problem identification: no sprints */}
                  {!isAnalyzing && sprints.length === 0 && tasks.length > 0 && (
                    <div className="flex items-start gap-2 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                      <Info className="h-4 w-4 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                      <p className="text-xs text-blue-800 dark:text-blue-200">
                        No sprints found. Tasks will be created without sprint assignment. You can assign them later.
                      </p>
                    </div>
                  )}
                  {/* Problem identification: few tasks */}
                  {!isAnalyzing && tasks.length > 0 && tasks.length < 3 && !rawSuggestions && (
                    <div className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                      <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                      <p className="text-xs text-amber-800 dark:text-amber-200">
                        Only {tasks.length} task{tasks.length !== 1 ? 's' : ''} extracted. Consider adding more detail to your scope document for better results.
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {step === 2 && (
            <div className="animate-fade-in-up py-6">
              {isApplying ? (
                <div className="text-center">
                  <Loader2 className="h-14 w-14 text-primary-500 animate-spin mx-auto mb-5" />
                  <h3 className="text-lg font-semibold text-secondary-900 dark:text-white mb-1">
                    Creating sprints and tasks
                  </h3>
                  <p className="text-sm text-secondary-500 mb-2">
                    Creating {sprints.length} sprint{sprints.length !== 1 ? 's' : ''} and {totalTasksToCreate} task{totalTasksToCreate !== 1 ? 's' : ''}...
                  </p>
                  <p className="text-xs text-secondary-400 mb-6">Do not close this window</p>
                  <div className="flex flex-col gap-3 max-w-sm mx-auto">
                    <div className={`flex items-center gap-3 p-3 rounded-lg transition-colors ${
                      applyStep >= 1 ? 'bg-primary-50 dark:bg-primary-900/20' : 'bg-secondary-50 dark:bg-secondary-900/30'
                    }`}>
                      {applyStep >= 1 ? <Loader2 className="h-5 w-5 animate-spin text-primary-500 flex-shrink-0" /> : (
                        <span className="w-5 h-5 rounded-full border-2 border-secondary-300 flex-shrink-0" />
                      )}
                      <span className={applyStep >= 1 ? 'text-primary-700 dark:text-primary-300 font-medium' : 'text-secondary-500'}>
                        Creating {sprints.length} sprint{sprints.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                    <div className={`flex items-center gap-3 p-3 rounded-lg transition-colors ${
                      applyStep >= 2 ? 'bg-primary-50 dark:bg-primary-900/20' : 'bg-secondary-50 dark:bg-secondary-900/30'
                    }`}>
                      {applyStep >= 2 ? <Loader2 className="h-5 w-5 animate-spin text-primary-500 flex-shrink-0" /> : (
                        <span className="w-5 h-5 rounded-full border-2 border-secondary-300 flex-shrink-0" />
                      )}
                      <span className={applyStep >= 2 ? 'text-primary-700 dark:text-primary-300 font-medium' : 'text-secondary-500'}>
                        Creating {totalTasksToCreate} task{totalTasksToCreate !== 1 ? 's' : ''}
                      </span>
                    </div>
                    <div className={`flex items-center gap-3 p-3 rounded-lg ${
                      applyStep >= 3 ? 'bg-green-50 dark:bg-green-900/20' : 'bg-secondary-50 dark:bg-secondary-900/30'
                    }`}>
                      {applyStep >= 3 ? <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" /> : (
                        <span className="w-5 h-5 rounded-full border-2 border-secondary-300 flex-shrink-0" />
                      )}
                      <span className={applyStep >= 3 ? 'text-green-700 dark:text-green-300 font-medium' : 'text-secondary-500'}>
                        {applyStep >= 3 ? 'Done' : 'Waiting...'}
                      </span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center max-w-md mx-auto">
                  <div className="w-20 h-20 rounded-2xl bg-primary-50 dark:bg-primary-900/30 flex items-center justify-center mx-auto mb-5">
                    <CheckCircle className="h-10 w-10 text-primary-500" />
                  </div>
                  <h3 className="text-xl font-semibold text-secondary-900 dark:text-white mb-2">
                    Ready to create
                  </h3>
                  <p className="text-sm text-secondary-600 dark:text-secondary-400 mb-6">
                    We&apos;ll add {sprints.length} sprint{sprints.length !== 1 ? 's' : ''} and {totalTasksToCreate} task{totalTasksToCreate !== 1 ? 's' : ''} to your project.
                  </p>
                  <div className="flex gap-4 justify-center text-sm">
                    <span className="px-3 py-1.5 rounded-lg bg-secondary-100 dark:bg-secondary-800 text-secondary-700 dark:text-secondary-300">
                      {sprints.length} sprint{sprints.length !== 1 ? 's' : ''}
                    </span>
                    <span className="px-3 py-1.5 rounded-lg bg-secondary-100 dark:bg-secondary-800 text-secondary-700 dark:text-secondary-300">
                      {totalTasksToCreate} task{totalTasksToCreate !== 1 ? 's' : ''}
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-secondary-200 dark:border-secondary-700 bg-secondary-50/30 dark:bg-secondary-900/20 flex justify-between items-center gap-4">
          {step > 0 && step < 2 && (
            <button
              onClick={() => {
                setAsyncTaskId(null);
                setStep(step - 1);
              }}
              className="btn-secondary flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </button>
          )}
          <div className="flex-1" />
          {step === 0 && (
            <button
              onClick={handleAnalyze}
              disabled={!file || isAnalyzing}
              className="btn-primary flex items-center gap-2 px-6 py-2.5 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isAnalyzing ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Analyzing...
                </>
              ) : (
                <>
                  Analyze
                  <ArrowRight className="h-4 w-4" />
                </>
              )}
            </button>
          )}
          {step === 1 && !isAnalyzing && (
            <button
              onClick={() => setStep(2)}
              disabled={tasks.length === 0}
              className="btn-primary flex items-center gap-2 px-6 py-2.5 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Continue
              <ArrowRight className="h-4 w-4" />
            </button>
          )}
          {step === 2 && (
            <button
              onClick={handleApply}
              disabled={applyMutation.isLoading}
              className="btn-primary flex items-center gap-2 px-6 py-2.5 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isApplying ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4" />
                  Create Tasks
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ScopeToTasksWizard;
